import React, {createContext, FC, ReactNode, useContext, useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'

// Define the context type
interface QueryManageListStatusType {
  currentPage: number
  currentSearch: string
  startDate: string
  endDate: string
  createDate: string
  user: string
  destinationCountry: string
  filters: Record<string, any>
  storageKey: string
  persist: boolean
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  setCurrentSearch: React.Dispatch<React.SetStateAction<string>>
  setStartDate: React.Dispatch<React.SetStateAction<string>>
  setEndDate: React.Dispatch<React.SetStateAction<string>>
  setCreateDate: React.Dispatch<React.SetStateAction<string>>
  setUser: React.Dispatch<React.SetStateAction<string>>
  setDestinationCountry: React.Dispatch<React.SetStateAction<string>>
  setFilter: (name: string, value: any) => void
}

// Create the context with a default value
const QueryManageListStatus = createContext<QueryManageListStatusType>({
  currentPage: 1,
  currentSearch: '',
  startDate: '',
  endDate: '',
  createDate: '',
  user: '',
  destinationCountry: '',
  filters: {},
  storageKey: '',
  persist: false,
  setCurrentPage: () => {},
  setCurrentSearch: () => {},
  setStartDate: () => {},
  setEndDate: () => {},
  setCreateDate: () => {},
  setUser: () => {},
  setDestinationCountry: () => {},
  setFilter: () => {},
})

interface ManageListStatusProps {
  children: ReactNode
  // Save list state in sessionStorage so it survives a page refresh
  persist?: boolean
}

const readStored = (key: string) => {
  try {
    return JSON.parse(sessionStorage.getItem(key) || '{}')
  } catch {
    return {}
  }
}

const writeStored = (key: string, value: unknown) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
  } catch {}
}

// Context provider component
const ManageListStatus: FC<ManageListStatusProps> = ({children, persist = true}) => {
  const {pathname} = useLocation()
  // Keyed by module (first path segment), so a refresh on a detail page restores its listing
  const [storageKey] = useState(() => `list-status:${pathname.split('/')[1]}`)
  const [stored] = useState(() => (persist ? readStored(storageKey) : {}))
  const [currentPage, setCurrentPage] = useState<number>(stored.currentPage ?? 1)
  const [currentSearch, setCurrentSearch] = useState<string>(stored.currentSearch ?? '')
  const [startDate, setStartDate] = useState<string>(stored.startDate ?? '')
  const [endDate, setEndDate] = useState<string>(stored.endDate ?? '')
  const [createDate, setCreateDate] = useState<string>(stored.createDate ?? '')
  const [user, setUser] = useState<string>(stored.user ?? '')
  const [destinationCountry, setDestinationCountry] = useState<string>(
    stored.destinationCountry ?? ''
  )
  const [filters, setFilters] = useState<Record<string, any>>(stored.filters ?? {})
  const setFilter = (name: string, value: any) => setFilters((prev) => ({...prev, [name]: value}))

  useEffect(() => {
    if (!persist) return
    writeStored(storageKey, {
      currentPage,
      currentSearch,
      startDate,
      endDate,
      createDate,
      user,
      destinationCountry,
      filters,
    })
  }, [
    currentPage,
    currentSearch,
    startDate,
    endDate,
    createDate,
    user,
    destinationCountry,
    filters,
  ])

  return (
    <QueryManageListStatus.Provider
      value={{
        currentPage,
        currentSearch,
        startDate,
        endDate,
        createDate,
        user,
        destinationCountry,
        filters,
        storageKey,
        persist,
        setCurrentPage,
        setCurrentSearch,
        setStartDate,
        setEndDate,
        setCreateDate,
        setUser,
        setDestinationCountry,
        setFilter,
      }}
    >
      {children}
    </QueryManageListStatus.Provider>
  )
}

// Custom hook to use the context
const useManageListStatus = () => useContext(QueryManageListStatus)

// A list filter's selected value (e.g. a dropdown option), remembered with the list state
const useListFilter = <T,>(name: string, initial: T): [T, (value: T) => void] => {
  const {filters, setFilter} = useManageListStatus()
  const value = name in filters ? (filters[name] as T) : initial
  return [value, (next: T) => setFilter(name, next)]
}

// A list's request state (page, filters, sort...), remembered across page refreshes
const usePersistedQueryState = <T,>(name: string, initial: T) => {
  const {storageKey, persist} = useManageListStatus()
  const key = `${storageKey}:${name}`
  const [state, setState] = useState<T>(() =>
    persist ? {...initial, ...readStored(key)} : initial
  )
  useEffect(() => {
    if (persist) writeStored(key, state)
  }, [state])
  return [state, setState] as const
}

export {ManageListStatus, useManageListStatus, useListFilter, usePersistedQueryState}
